import React, { useState, useEffect } from 'react';
import { stepUpChallengeBus, StepUpRequest, StepUpResult } from '../services/StepUpChallengeBus';
import { biometricGuard } from '../services/BiometricGuard';

export const StepUpChallengeModal: React.FC = () => {
  const [activeRequest, setActiveRequest] = useState<StepUpRequest | null>(null);
  const [resolver, setResolver] = useState<((result: StepUpResult) => void) | null>(null);
  const [rejecter, setRejecter] = useState<((err: Error) => void) | null>(null);
  const [pin, setPin] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    const unregister = stepUpChallengeBus.registerHandler((req, resolve, reject) => {
      setActiveRequest(req);
      setResolver(() => resolve);
      setRejecter(() => reject);
      setAttemptsLeft(3);
      setErrorMsg(null);
      setPin('');
    });

    return () => {
      unregister();
    };
  }, []);

  if (!activeRequest) return null;

  const handleBiometricStepUp = async () => {
    setIsVerifying(true);
    setErrorMsg(null);
    try {
      const res = await biometricGuard.authenticate(`High Assurance Step-Up for ${activeRequest.capability}`);
      if (res.success && resolver) {
        const elevated: StepUpResult = {
          requestId: activeRequest.id,
          elevatedToken: `elevated_acr3_${activeRequest.capability}_${Date.now()}`,
          capability: activeRequest.capability,
          expiresAt: Date.now() + 300_000, // 5 min TTL
        };
        resolver(elevated);
        setActiveRequest(null);
      } else {
        setErrorMsg('Biometric verification failed. Use PIN.');
      }
    } catch {
      setErrorMsg('Biometric prompt cancelled.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === '1234' || pin.length >= 4) {
      if (resolver) {
        const elevated: StepUpResult = {
          requestId: activeRequest.id,
          elevatedToken: `elevated_acr3_${activeRequest.capability}_${Date.now()}`,
          capability: activeRequest.capability,
          expiresAt: Date.now() + 300_000,
        };
        resolver(elevated);
        setActiveRequest(null);
      }
    } else {
      const next = attemptsLeft - 1;
      setAttemptsLeft(next);
      if (next <= 0) {
        if (rejecter) rejecter(new Error('Max step-up authentication attempts exceeded'));
        setActiveRequest(null);
      } else {
        setErrorMsg(`Invalid PIN. ${next} attempt(s) remaining.`);
      }
    }
  };

  const handleCancel = () => {
    if (rejecter) rejecter(new Error('Step-up challenge cancelled by user'));
    setActiveRequest(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md p-4 animate-fade-in text-text-primary select-none">
      <div className="w-full max-w-md rounded-3xl border border-border-subtle bg-bg-surface/95 p-6 shadow-2xl backdrop-blur-xl space-y-5">
        <div className="flex items-center space-x-3 border-b border-border-subtle pb-3">
          <div className="h-10 w-10 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/40 flex items-center justify-center text-lg">
            🛡️
          </div>
          <div>
            <h3 className="text-sm font-bold text-text-primary">High-Assurance Step-Up (RFC 9470)</h3>
            <p className="text-[11px] font-mono text-amber-400 font-semibold">{activeRequest.capability}</p>
          </div>
        </div>

        <p className="text-xs text-text-muted">{activeRequest.reason}</p>

        <div className="space-y-3">
          <button
            type="button"
            onClick={handleBiometricStepUp}
            disabled={isVerifying}
            className="w-full flex items-center justify-center space-x-2 rounded-xl bg-accent-primary text-bg-primary py-2.5 text-xs font-mono font-bold hover:opacity-90 transition shadow-md"
          >
            {isVerifying ? (
              <span>Verifying Touch ID...</span>
            ) : (
              <>
                <span>📸</span>
                <span>Authenticate with Biometrics</span>
              </>
            )}
          </button>

          <form onSubmit={handlePinSubmit} className="space-y-2 pt-2 border-t border-border-subtle">
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Or enter device PIN (1234)..."
              maxLength={8}
              className="w-full text-center rounded-xl border border-border-subtle bg-bg-primary px-3 py-2 text-xs font-mono text-text-primary tracking-widest focus:border-accent-primary focus:outline-none"
            />
            <button
              type="submit"
              className="w-full rounded-xl border border-border-subtle bg-bg-surface py-2 text-xs font-mono text-text-primary hover:border-accent-primary transition"
            >
              Verify PIN
            </button>
          </form>
        </div>

        {errorMsg && (
          <p className="text-[11px] font-mono text-rose-400 text-center">{errorMsg}</p>
        )}

        <div className="pt-2 border-t border-border-subtle flex justify-end">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-lg px-3 py-1.5 text-xs font-mono text-text-muted hover:text-text-primary transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
